import React, { useState } from 'react';
import GeneralIcon from '../assets';
import contaService from '../services/contaService';
import { buildAccountPayload } from '../models/contaModel';
import BackWithSidebar from '../components/BackWithSidebar';

export default function ContaDoVendedor() {
  const [name, setName] = useState('');
  const [idNumber, setIdNumber] = useState(''); // stores only digits for CPF/CNPJ
  const [idType, setIdType] = useState('cpf'); // 'cpf' or 'cnpj'
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  function onSelectFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    const url = URL.createObjectURL(file);
    setAvatarPreview(url);
  }

  function onlyDigits(value) {
    return value.replace(/\D/g, '');
  }

  function formatCPF(v) {
    const d = onlyDigits(v).slice(0, 11);
    return d.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (m, a, b, c, d2) => {
      return `${a}.${b}.${c}${d2 ? '-' + d2 : ''}`;
    });
  }

  function formatCNPJ(v) {
    const d = onlyDigits(v).slice(0, 14);
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, (m, a, b, c, d1, d2) => {
      return `${a}.${b}.${c}/${d1}${d2 ? '-' + d2 : ''}`;
    });
  }

  function handleIdChange(e) {
    const raw = onlyDigits(e.target.value);
    const max = idType === 'cpf' ? 11 : 14;
    setIdNumber(raw.slice(0, max));
  }

  function toggleIdType() {
    setIdType(t => (t === 'cpf' ? 'cnpj' : 'cpf'));
    setIdNumber('');
  }

  function formatPhone(v) {
    const d = onlyDigits(v);
    if (!d) return '';
    // if more than 11 digits assume country code present
    if (d.length > 11) {
      const cc = d.slice(0, d.length - 11);
      const area = d.slice(-11, -9);
      const n1 = d.slice(-9, -4);
      const n2 = d.slice(-4);
      return `+${cc} (${area}) ${n1}-${n2}`;
    }
    if (d.length === 11) {
      const area = d.slice(0, 2);
      const n1 = d.slice(2, 7);
      const n2 = d.slice(7);
      return `(${area}) ${n1}-${n2}`;
    }
    if (d.length === 10) {
      const area = d.slice(0, 2);
      const n1 = d.slice(2, 6);
      const n2 = d.slice(6);
      return `(${area}) ${n1}-${n2}`;
    }
    return d;
  }

  function handlePhoneChange(e) {
    const raw = onlyDigits(e.target.value);
    // limit to 15 digits (country + area + number)
    setPhone(raw.slice(0, 15));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = buildAccountPayload({ name, cpf: idNumber, email, phone, password, avatarFile });
      await contaService.saveAccount(payload);
      alert('Dados enviados com sucesso');
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar: ' + (err.message || err));
    } finally { setLoading(false); }
  }

  async function onLogout() {
    try { await contaService.logout(); window.location.href = './'; } catch (err) { alert('Erro no logout'); }
    // Clear local auth and navigate to login. Attempt server logout but do not block on it.
    try {
      // attempt server-side logout but ignore errors
      contaService.logout().catch(() => {});
    } catch (e) {}
    try { window.localStorage.removeItem('authToken'); } catch (e) {}
    window.location.href = '/';
  }

  async function onDelete() {
    if (!window.confirm('Deseja realmente excluir a conta?')) return;
      try {
        await contaService.deleteAccount();
        // after successful delete, clear auth and redirect to login
        try { window.localStorage.removeItem('authToken'); } catch (e) {}
        alert('Conta excluída');
        window.location.href = '/';
      } catch (err) {
        console.error(err);
        alert('Erro ao excluir: ' + (err.message || err));
      }
  }

  return (
    <div className="p-4 max-w-md mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button className="p-2 rounded hover:bg-gray-100" onClick={() => window.history.back()}>
          <GeneralIcon iconName="arrow-left" className="w-5 h-5" />
        </button>
        <BackWithSidebar to="/dashboard" className="p-2 rounded-full hover:bg-gray-100" />
        <h1 className="text-lg font-semibold">Alterar informações da conta</h1>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <div className="text-sm font-medium mb-2">Foto</div>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-gray-50 rounded-md flex items-center justify-center overflow-hidden">
              {avatarPreview ? (
                <img src={avatarPreview} alt="avatar" className="object-cover w-full h-full" />
              ) : (
                <GeneralIcon iconName="userroxo" className="w-10 h-10 text-gray-400" />
              )}
            </div>

            <label className="flex-1">
              <div className="border-2 border-dashed rounded-lg border-purple-300 p-3 flex items-center gap-3">
                <div className="flex-1 text-sm text-purple-700">Alterar a foto de perfil</div>
                <div className="p-2 rounded">
                  <GeneralIcon iconName="camera" className="w-10 h-10 text-purple-700" />
                </div>
              </div>
              <input type="file" accept="image/*" onChange={onSelectFile} className="hidden" />
            </label>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium">Nome</label>
          <input className="w-full border rounded px-3 py-2" value={name} onChange={e => setName(e.target.value)} />
        </div>

        <div>
          <label className="block text-xs font-medium">Identificação</label>
          <div className="flex gap-2 items-center">
            <button type="button" onClick={toggleIdType} className="w-24 text-xs bg-gray-50 border rounded px-2 py-2 flex items-center justify-center">
              {idType === 'cpf' ? 'CPF' : 'CNPJ'}
            </button>
            <input
              className="flex-1 border rounded px-3 py-2"
              value={idType === 'cpf' ? formatCPF(idNumber) : formatCNPJ(idNumber)}
              onChange={handleIdChange}
              placeholder={idType === 'cpf' ? '000.000.000-00' : '00.000.000/0000-00'}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium">E-mail</label>
          <input type="email" className="w-full border rounded px-3 py-2" value={email} onChange={e => setEmail(e.target.value)} />
        </div>

        <div>
          <label className="block text-xs font-medium">Telefone</label>
          <input className="w-full border rounded px-3 py-2" value={formatPhone(phone)} onChange={handlePhoneChange} placeholder="(11) 91234-5678" />
        </div>

        <div>
          <label className="block text-xs font-medium">Redefinir senha</label>
          <input type="password" className="w-full border rounded px-3 py-2" value={password} onChange={e => setPassword(e.target.value)} />
        </div>

        <div>
          <button type="submit" className="w-full py-3 rounded bg-purple-400 hover:bg-purple-600 text-white">
            {loading ? 'Salvando...' : 'Confirmar alterações'}
          </button>
        </div>

        <div className="mt-4 space-y-2">
          <button type="button" className="w-full py-3 rounded border border-red-200 text-red-600 flex items-center justify-center gap-2" onClick={onLogout}>
            <GeneralIcon iconName="logout" className="w-4 h-4" /> Sair da conta
          </button>
          <button type="button" className="w-full py-3 rounded border border-red-200 text-red-600 flex items-center justify-center gap-2" onClick={onDelete}>
            <GeneralIcon iconName="lixeira" className="w-4 h-4" /> Excluir conta
          </button>
        </div>
      </form>
    </div>
  );
}
